import { type DocumentDetails } from "~/api/types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import i18n from "~/i18n";
import { z } from "zod";
import { type UpdateDocumentData } from "~/api/v2/requests";
import {
  tierIdFormValueToNumberOrNull,
  tierIdToFormValue,
} from "~/helpers/tier";

interface Props {
  document: DocumentDetails;
}

export type DocumentEditFormValues = z.infer<typeof formSchema>;

const formSchema = z.object({
  ownerId: z.string({ message: i18n.t(($) => $.validations.required) }),
  publisherId: z.string().optional(),
  categoryId: z.string({ message: i18n.t(($) => $.validations.required) }),
  tierId: z.string({ message: i18n.t(($) => $.validations.required) }),
  referenceNumber: z.string().trim().max(50).nullable(),
  tags: z.array(z.string()),
  scheduleReviewPeriod: z.string().nullable(),
  linkedDocuments: z.array(z.object({ value: z.string(), label: z.string() })),
});

const getDocumentEditFormDefaultValues = (
  document: DocumentDetails,
): DocumentEditFormValues => ({
  ownerId: document.owner.userId.toString(),
  publisherId: document.publisher?.userId.toString(),
  categoryId: document.category.id.toString(),
  tierId: tierIdToFormValue(document.tier?.id),
  referenceNumber: document.referenceNumber ?? null,
  tags: document.tags.map((tag) => tag.id.toString()),
  scheduleReviewPeriod: document.scheduleReviewPeriod ?? null,
  linkedDocuments: document.linkedDocuments.map((doc) => ({
    value: doc.id.toString(),
    label: doc.title,
  })),
});

export function useDocumentEditForm({ document }: Props) {
  const form = useForm<DocumentEditFormValues>({
    values: getDocumentEditFormDefaultValues(document),
    resolver: zodResolver(formSchema),
  });

  const filterUnchangedValues = (
    values: DocumentEditFormValues,
  ): UpdateDocumentData => {
    const result: UpdateDocumentData = {};

    // Category
    if (values.categoryId !== document.category.id.toString()) {
      result.categoryId = Number(values.categoryId);
    }

    // Tier
    if (values.tierId !== tierIdToFormValue(document.tier?.id)) {
      result.tierId = tierIdFormValueToNumberOrNull(values.tierId);
    }

    // Reference Number
    if (values.referenceNumber !== (document.referenceNumber ?? null)) {
      result.referenceNumber = values.referenceNumber;
    }

    // Owner
    if (values.ownerId !== document.owner.userId.toString()) {
      result.ownerId = Number(values.ownerId);
    }

    // Publisher
    if (values.publisherId !== document.publisher?.userId.toString()) {
      result.publisherId = values.publisherId
        ? Number(values.publisherId)
        : undefined;
    }

    if (values.scheduleReviewPeriod !== document.scheduleReviewPeriod) {
      result.scheduleReviewPeriod = values.scheduleReviewPeriod;
    }

    // Tags
    const newTags = values.tags.map((tag) => Number(tag));
    const currentTags = document.tags.map((tag) => tag.id);

    if (xor(newTags, currentTags).length > 0) {
      result.tagIds = newTags;
    }

    // Linked Documents
    const newLinkedDocuments = values.linkedDocuments.map((d) =>
      Number(d.value),
    );

    const currentLinkedDocuments = document.linkedDocuments.map((d) => d.id);

    if (xor(newLinkedDocuments, currentLinkedDocuments).length > 0) {
      result.linkedDocumentIds = newLinkedDocuments;
    }

    return result;
  };

  return { form, filterUnchangedValues };
}

const xor = (a: number[], b: number[]) => {
  const setA = new Set(a);
  const setB = new Set(b);

  return [
    ...a.filter((value) => !setB.has(value)),
    ...b.filter((value) => !setA.has(value)),
  ];
};
